import { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';

import { DEFAULT_DATE_FORMAT } from 'pages/resource/constants';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useTimeZone } from 'hooks/use-timezone.hook';
import { searchParams as SearchParams } from 'shared/types/search-params';

interface UseFloorplanMapDateValues {
  /** The day the map shows availability for, `YYYY-MM-DD`. Never empty. */
  date: string;
  /** Whether that day came from the url or is only the fallback. */
  isDateSelected: boolean;
  /** Today in the building's timezone, `YYYY-MM-DD`. */
  getToday: () => string;
}

/**
 * The day the floorplan shows availability for.
 *
 * Read from the url rather than held in component state: the miniapp already keeps the booking
 * date/time selection in the query string, and the existing date picker writes it there. A second
 * source of truth would have to be reconciled with it as soon as the map grows date filters.
 *
 * (The admin builder in hqos keeps its own `useState<Date>` because it has no such url state — the
 * shared part of that pattern is normalizing to a date-only string and letting it key the query.)
 *
 * Falls back to today in the *building's* timezone: late evening UTC is already tomorrow in some
 * buildings, and the map must agree with the day the server answers for.
 */
export const useFloorplanMapDate = (): UseFloorplanMapDateValues => {
  const timezone = useTimeZone();
  // `startDates` is a comma-separated multi-day selection (see `getCalendarViewDateParams`).
  const { startDate, startDates } = useSearchParams<SearchParams>();

  const getToday = useCallback(() => moment().tz(timezone).format(DEFAULT_DATE_FORMAT), [timezone]);

  const selectedDate = useMemo(() => {
    // On a multi-day selection the map can only show one day; the first is the one the flow treats
    // as the start of the range.
    const selected = startDate ?? startDates?.split(',')[0]?.trim();

    return selected && moment(selected, DEFAULT_DATE_FORMAT, true).isValid() ? selected : null;
  }, [startDate, startDates]);

  return useMemo(
    () => ({ date: selectedDate ?? getToday(), isDateSelected: !!selectedDate, getToday }),
    [selectedDate, getToday],
  );
};
